/**
 * Phase 6-A2: MULTI BS whitelist WP — non-first Period Tab opening/b/f ← prior Tab c/f.
 * Display/session linkage only; SINGLE unchanged (#20).
 */
import {computePpeCalculatedDepnChgFinal} from '@/adjustment/adjust/dialogs/asset/sub/ppeDepnFormula';
import {
    getMergedLeaseDateBounds,
    hasActiveRenewal,
    normalizeRentFreePeriods,
    computeRentalRowAllDetailsTotal,
} from '@/adjustment/adjust/dialogs/expenses/sub/rental-utils';
import {overlapMonthsInclusive, parseYMD} from '@/utils/dataUtils';

const PPE_ROW_ARRAY_KEYS = [
    'locationOfPropertyRow',
    'commercialAllowanceRow',
    'industrialAllowanceRow',
    'furnitureFixtureRow',
    'computerHardSoftRow',
    'fullPayMotorRow',
    'hirePurchaseMotorRow',
    'machineryRow',
    'liftEscalatorRow',
    'equipmentRow',
] as const;

/** ITL (BS-NCA-ITL) section row arrays — parallel to PPE categories. */
const ITL_ROW_ARRAY_KEYS = [
    'taxiLicenseRow',
    'fullPayTaxiCabRow',
    'hirePurchaseTaxiCabRow',
] as const;

const PPE_RATE_BY_ROW_KEY: Record<(typeof PPE_ROW_ARRAY_KEYS)[number], string> = {
    locationOfPropertyRow: 'locationOfPropertyRate',
    commercialAllowanceRow: 'commercialAllowanceRate',
    industrialAllowanceRow: 'industrialAllowanceRate',
    furnitureFixtureRow: 'furnitureFixtureRate',
    computerHardSoftRow: 'computerHardSoftRate',
    fullPayMotorRow: 'fullPayMotorRate',
    hirePurchaseMotorRow: 'hirePurchaseMotorRate',
    machineryRow: 'machineryRate',
    liftEscalatorRow: 'liftEscalatorRate',
    equipmentRow: 'equipmentRate',
};

function normItem(v: unknown): string {
    return String(v ?? '').trim().toLowerCase();
}

function num(v: unknown): number {
    return Number(v) || 0;
}

function nearlyEq(a: number, b: number): boolean {
    return Math.abs(a - b) < 0.005;
}

/** Match prior row: prefer same item name; else same index among named/non-empty rows. */
function findPriorRow<T extends {item?: string | null}>(
    curr: T,
    priorRows: T[],
    currIndex: number,
): T | null {
    const name = normItem(curr?.item);
    if (name) {
        const byName = priorRows.find((r) => normItem(r?.item) === name);
        if (byName) return byName;
    }
    return priorRows[currIndex] ?? null;
}

function ratePercentToFraction(rate: number): number {
    const r = Number(rate) || 0;
    return r > 1 ? r / 100 : r;
}

/** Align with IntangibleSubTable.computeIntangibleDerived (no UI imports). */
function computeIaClosingCf(row: any): {costCf: number; accAmorCf: number; nbvCf: number} {
    const costCf = num(row?.costBf) + num(row?.addition) + num(row?.costDisposal);
    const accAmorCf = num(row?.accAmorBf) + num(row?.amorChg) + num(row?.otherValue) + num(row?.accDisposal);
    return {costCf, accAmorCf, nbvCf: costCf - accAmorCf};
}

export type PpePriorCfContext = {
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
    /** ppeRate object from prior (or current) detail */
    ppeRate?: Record<string, number | null | undefined> | null;
};

/**
 * MULTI rolled display: Cost b/f ← prior costBf or additions (non-zero end).
 * Prefer costBf when both are non-zero; fall back to whichever side is set.
 */
function pickPpeCostBfFromPriorCostOrAdditions(row: any): number | null {
    const costBf = row?.costBf;
    const additions = row?.additions;
    if (costBf != null && Number(costBf) !== 0) return costBf;
    if (additions != null && Number(additions) !== 0) return additions;
    if (costBf != null) return costBf;
    if (additions != null) return additions;
    return null;
}

/**
 * MULTI IA/ITL: Cost b/f ← prior costBf or addition (non-zero end).
 * Same rule as PPE (`additions` → IA field name `addition`).
 */
function pickIaCostBfFromPriorCostOrAddition(row: any): number | null {
    const costBf = row?.costBf;
    const addition = row?.addition;
    if (costBf != null && Number(costBf) !== 0) return costBf;
    if (addition != null && Number(addition) !== 0) return addition;
    if (costBf != null) return costBf;
    if (addition != null) return addition;
    return null;
}

/** True when row is a MULTI rolled display (Cost b/f carries prior costBf/additions). */
function isPpeCostBfRolled(row: any): boolean {
    return !!(row?.ppeCostBfIsCostBf || row?.ppeCostBfIsPriorNbv);
}

/**
 * MULTI: Cost c/f === 0 (or sticky {@code ppeSuppressLaterRoll}) → do not roll this
 * lineage onto later Period Tabs. Q4B: suppress flag sticks after Cost c/f recovers.
 */
export function ppeRowBlocksLaterRoll(
    row: any,
    depRateFraction: number,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): boolean {
    if (!row || typeof row !== 'object') return false;
    if (row.ppeSuppressLaterRoll) return true;
    if (!String(row.item ?? '').trim()) return false;
    return computePpeRowClosingCf(row, depRateFraction, periodFrom, periodTo).costCf === 0;
}

/** Effective dep rate fraction for one PPE category from a Tab's ppeRate. */
function ppeEffectiveDepRateForRowKey(
    detail: any,
    rowKey: (typeof PPE_ROW_ARRAY_KEYS)[number],
): number {
    const rates = detail?.ppeRate || {};
    const rateKey = PPE_RATE_BY_ROW_KEY[rowKey];
    const rawRate = Number(rates?.[rateKey] ?? 0) || 0;
    const depRate = ratePercentToFraction(rawRate);
    if (rowKey !== 'equipmentRow') return depRate;
    return ratePercentToFraction(Number(rates?.equipmentIrdRate ?? rawRate) || 0);
}

/** Cost / Acc dep c/f for one PPE row (aligned with PropertySubTable.computeRow, no field-adj). */
export function computePpeRowClosingCf(
    row: any,
    depRateFraction: number,
    periodFrom: string | null | undefined,
    periodTo: string | null | undefined,
): {costCf: number; accDepCf: number; nbvCf: number} {
    // Legacy MULTI: costBf held NBV — reconstruct Cost. New MULTI (`ppeCostBfIsCostBf`) / unflagged: costBf is Cost.
    const basis = row?.ppeCostBfIsPriorNbv && !row?.ppeCostBfIsCostBf
        ? {...row, costBf: num(row.costBf) + num(row.accDepBf)}
        : row;
    const costBf = num(basis?.costBf);
    const additions = num(basis?.additions);
    const disposalCost = num(basis?.disposalCost);
    const accDepBf = num(basis?.accDepBf);
    const costCf = costBf + additions + disposalCost;
    const additionsEmpty = basis?.additions == null;
    const calculated = depRateFraction
        ? computePpeCalculatedDepnChgFinal({
            costBf,
            additions,
            accDepBf,
            additionsEmpty,
            date: basis?.date,
            depRate: depRateFraction,
            periodFrom,
            periodTo,
        })
        : 0;
    const depn = basis?.depnChgChange != null ? num(basis.depnChgChange) : calculated;
    const disposalDep = disposalCost < 0 ? -(accDepBf + depn) : 0;
    const accDepCf = accDepBf + depn + disposalDep;
    return {costCf, accDepCf, nbvCf: costCf - accDepCf};
}

function isBlankPpeRow(row: any): boolean {
    if (!row || typeof row !== 'object') return true;
    const hasItem = String(row.item || '').trim() !== '';
    const hasCost = row.costBf != null && Number(row.costBf) !== 0;
    const hasAdd = row.additions != null && Number(row.additions) !== 0;
    const hasDep = row.accDepBf != null && Number(row.accDepBf) !== 0;
    const hasSavedId = row.id != null && Number(row.id) > 0;
    return !hasSavedId && (!hasItem || (!hasCost && !hasAdd && !hasDep));
}

function isBlankPpeRows(rows: any[] | null | undefined): boolean {
    if (!Array.isArray(rows) || rows.length === 0) return true;
    return rows.every(isBlankPpeRow);
}

function priorRowHasCarryForwardContent(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (String(row.item || '').trim()) return true;
    if (row.costBf != null && Number(row.costBf) !== 0) return true;
    if (row.additions != null && Number(row.additions) !== 0) return true;
    if (row.accDepBf != null && Number(row.accDepBf) !== 0) return true;
    if (row.disposalCost != null && Number(row.disposalCost) !== 0) return true;
    return false;
}

/**
 * Origin row on a Tab: user entered date here (not a rolled-forward copy).
 * Forward pass of date/item/costBf/additions/accDepBf only starts after date is set.
 */
function isPpeOriginRow(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (!row.date) return false;
    if (isPpeCostBfRolled(row)) return false;
    return true;
}

/**
 * Stable origin identity for MULTI roll / delete sync.
 * Id-based keys are globally unique; index fallback uses date+item.
 * Cross-SubTable isolation on delete is enforced by {@link removePpeOriginFromLaterTabs}
 * {@code sourceRowKey} — do not embed rowKey here or later-tab rolls with legacy keys stop matching.
 */
function ppeOriginKey(sliceKey: string, row: any, index: number): string {
    if (row?.ppeOriginKey) return String(row.ppeOriginKey);
    if (row?.id != null && Number(row.id) !== 0) return `${sliceKey}#id:${row.id}`;
    return `${sliceKey}#i:${index}|${row?.date || ''}|${normItem(row?.item)}`;
}

/** Public helper: stable origin identity for MULTI forward-roll / delete sync. */
export function buildPpeOriginKey(sliceKey: string, row: any, index: number): string {
    return ppeOriginKey(sliceKey, row, index);
}

export type PpePeriodSlice = {
    key: string;
    periodFrom?: string;
    periodTo?: string;
};

/** True when any PPE category has carry-forward content (item / amounts). */
export function ppeDetailHasCarryContent(detail: any): boolean {
    if (!detail || typeof detail !== 'object') return false;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        if (rows.some(priorRowHasCarryForwardContent)) return true;
    }
    return false;
}

/** True when any PPE category already has a DB-backed row (id > 0). */
export function ppeDetailHasPersistedRows(detail: any): boolean {
    if (!detail || typeof detail !== 'object') return false;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        if (rows.some((row: any) => row?.id != null && Number(row.id) > 0)) return true;
    }
    return false;
}

/** Fingerprint of dated origin rows across all Tabs before active — drives re-roll when any prior Tab changes. */
export function ppeAllPriorOriginsFingerprint(
    periods: PpePeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): string {
    if (!Array.isArray(periods) || !activeSliceKey || !apiCode) return '';
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex <= 0) return '';
    const payload: unknown[] = [];
    for (let i = 0; i < activeIndex; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        for (const rowKey of PPE_ROW_ARRAY_KEYS) {
            const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
            rows.forEach((row: any, index: number) => {
                if (!isPpeOriginRow(row)) return;
                payload.push({
                    k: ppeOriginKey(slice.key, row, index),
                    rowKey,
                    date: row.date,
                    item: row.item,
                    costBf: row.costBf,
                    additions: row.additions,
                    accDepBf: row.accDepBf,
                    disposalCost: row.disposalCost,
                    depnChgChange: row.depnChgChange,
                    suppress: !!row.ppeSuppressLaterRoll,
                    costCf: computePpeRowClosingCf(
                        row,
                        ppeEffectiveDepRateForRowKey(detail, rowKey),
                        slice.periodFrom,
                        slice.periodTo,
                    ).costCf,
                });
            });
        }
    }
    return JSON.stringify(payload);
}

/** Fingerprint of PPE row arrays — include in roll-forward signature to avoid stale-skip races. */
export function ppeDetailRowsFingerprint(detail: any): string {
    if (!detail || typeof detail !== 'object') return '';
    const payload: Record<string, unknown> = {};
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        payload[rowKey] = detail[rowKey];
    }
    return JSON.stringify(payload);
}

/**
 * Walk back from active slice to find the nearest prior draft that has PPE content.
 * `periodSlicesToApply` = [that prior's period, …, immediate previous period] — each hop
 * computes Cost/Acc dep c/f → next Cost/Acc dep b/f so skipped blank tabs still chain correctly.
 */
export function resolveNearestPriorPpeDetail(
    periods: PpePeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): {
    priorDetail: any;
    priorKey: string;
    periodSlicesToApply: Array<{periodFrom?: string; periodTo?: string}>;
    index: number;
} | null {
    if (!Array.isArray(periods) || periods.length < 2 || !activeSliceKey || !apiCode) return null;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex <= 0) return null;

    for (let i = activeIndex - 1; i >= 0; i--) {
        const prior = periods[i];
        if (!prior?.key) continue;
        const priorDetail = getDetail(`${apiCode}__${prior.key}`);
        if (!ppeDetailHasCarryContent(priorDetail)) continue;

        const periodSlicesToApply = periods
            .slice(i, activeIndex)
            .map((p) => ({periodFrom: p.periodFrom, periodTo: p.periodTo}));

        return {
            priorDetail,
            priorKey: prior.key,
            periodSlicesToApply,
            index: i,
        };
    }
    return null;
}

/** Roll one PPE origin row forward N period hops:
 * each hop:
 *   date/item ← origin (locked on later Tabs)
 *   costBf    ← prior costBf/additions (non-zero end)
 *   accDepBf  ← prior Acc dep c/f
 *   additions ← null
 */
function rollPpeRowAcrossPeriods(
    prior: any,
    effectiveRate: number,
    periodSlices: Array<{periodFrom?: string; periodTo?: string}>,
    seedId: number,
    originKey?: string,
): any {
    let row: any = {
        id: seedId,
        date: prior.date ?? null,
        item: prior.item ?? '',
        costBf: prior.costBf ?? null,
        additions: prior.additions ?? null,
        disposalCost: prior.disposalCost ?? null,
        accDepBf: prior.accDepBf ?? null,
        depnChgChange: prior.depnChgChange ?? null,
        ...(isPpeCostBfRolled(prior) ? {ppeCostBfIsCostBf: true} : {}),
        ...(originKey ? {ppeOriginKey: originKey} : {}),
    };

    for (const p of periodSlices) {
        const cf = computePpeRowClosingCf(row, effectiveRate, p.periodFrom, p.periodTo);
        const costBfCarry = pickPpeCostBfFromPriorCostOrAdditions(row);
        row = {
            id: seedId,
            date: prior.date ?? null,
            item: prior.item ?? '',
            costBf: costBfCarry,
            additions: null,
            disposalCost: null,
            accDepBf: cf.accDepCf,
            ppeCostBfIsCostBf: true,
            ...(originKey ? {ppeOriginKey: originKey} : {}),
        };
    }
    return row;
}

type OriginRoll = {
    key: string;
    rolled: any;
};

/** True when {@code row} matches an origin identity (ppeOriginKey or item+date). */
function ppeRowMatchesOriginIdentity(
    row: any,
    originKey: string,
    originRow: {date?: string | null; item?: string | null},
): boolean {
    if (row?.ppeOriginKey && String(row.ppeOriginKey) === originKey) return true;
    const delItem = normItem(originRow?.item);
    if (!delItem || normItem(row?.item) !== delItem) return false;
    const delDate = originRow?.date ?? null;
    if (delDate && row?.date && (row.date ?? null) !== delDate) return false;
    return true;
}

/**
 * Origin at Tab i must not appear on Tabs after a blocking Tab
 * (Cost c/f === 0 or sticky suppress on the origin or any intermediate match).
 */
function ppeOriginBlockedForActiveTab(
    rowKey: (typeof PPE_ROW_ARRAY_KEYS)[number],
    periods: PpePeriodSlice[],
    originIndex: number,
    activeIndex: number,
    originKey: string,
    originRow: any,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): boolean {
    for (let j = originIndex; j < activeIndex; j++) {
        const slice = periods[j];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        const rate = ppeEffectiveDepRateForRowKey(detail, rowKey);
        const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
        if (j === originIndex) {
            if (ppeRowBlocksLaterRoll(originRow, rate, slice.periodFrom, slice.periodTo)) {
                return true;
            }
            continue;
        }
        for (const row of rows) {
            if (!ppeRowMatchesOriginIdentity(row, originKey, originRow)) continue;
            if (ppeRowBlocksLaterRoll(row, rate, slice.periodFrom, slice.periodTo)) {
                return true;
            }
        }
    }
    return false;
}

function collectOriginRollsForCategory(
    rowKey: (typeof PPE_ROW_ARRAY_KEYS)[number],
    periods: PpePeriodSlice[],
    activeIndex: number,
    getDetail: (draftKey: string) => any,
    apiCode: string,
    seedIdRef: {current: number},
): OriginRoll[] {
    const out: OriginRoll[] = [];
    for (let i = 0; i < activeIndex; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        const priorRows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
        if (priorRows.length === 0) continue;

        const effectiveRate = ppeEffectiveDepRateForRowKey(detail, rowKey);
        const slices = periods
            .slice(i, activeIndex)
            .map((p) => ({periodFrom: p.periodFrom, periodTo: p.periodTo}));

        priorRows.forEach((prior: any, index: number) => {
            if (!isPpeOriginRow(prior)) return;
            const key = ppeOriginKey(slice.key, prior, index);
            // Cost c/f === 0 / suppress on origin or intermediate Tab → skip later Tabs
            if (ppeOriginBlockedForActiveTab(
                rowKey, periods, i, activeIndex, key, prior, getDetail, apiCode,
            )) {
                return;
            }
            const rolled = rollPpeRowAcrossPeriods(
                prior,
                effectiveRate,
                slices,
                seedIdRef.current--,
                key,
            );
            out.push({key, rolled});
        });
    }
    return out;
}

function findOriginRollForRow(row: any, originRolls: OriginRoll[]): OriginRoll | null {
    if (!originRolls.length) return null;
    const byKey = row?.ppeOriginKey
        ? originRolls.find((o) => o.key === row.ppeOriginKey)
        : null;
    if (byKey) return byKey;
    const name = normItem(row?.item);
    const date = row?.date ?? null;
    if (name || date) {
        const byDateItem = originRolls.find((o) => {
            const sameDate = (o.rolled?.date ?? null) === date;
            const sameItem = normItem(o.rolled?.item) === name;
            // Prefer strong match: date+item; allow item-only when both named
            if (name && date) return sameDate && sameItem;
            if (name) return sameItem;
            return sameDate && !normItem(o.rolled?.item);
        });
        if (byDateItem) return byDateItem;
    }
    return null;
}

function applyRolledDisplayFields(row: any, match: OriginRoll): any {
    const rolled = match.rolled;
    const next = {
        ...row,
        date: rolled.date ?? null,
        item: rolled.item ?? '',
        costBf: rolled.costBf,
        additions: null,
        accDepBf: rolled.accDepBf,
        disposalCost: row.disposalCost ?? null,
        ppeCostBfIsCostBf: true,
        ppeOriginKey: match.key,
    };
    delete next.ppeCostBfIsPriorNbv;
    return next;
}

/**
 * MULTI forward roll onto the active Tab from ALL earlier Tabs' origin rows
 * (dated, non-rolled). Forward-only: earlier Tabs never receive later Tab rows.
 *
 * - Tab N origin (date entered) → Tabs N+1… get a single display row
 *   (date/item + costBf←prior costBf/additions non-zero end,
 *    accDepBf←Acc dep c/f, additions cleared)
 * - Local origins that do not match any prior origin stay editable on this Tab
 * - Cost c/f === 0 / sticky suppress on a prior Tab → matching later-Tab rows dropped
 */
export function applyPpeForwardRollFromPriors(
    currentDetail: any,
    periods: PpePeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): any | null {
    if (!currentDetail || !Array.isArray(periods) || !activeSliceKey || !apiCode) return null;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex <= 0) return null;

    let changed = false;
    const next: any = {...currentDetail};
    const seedIdRef = {current: -1};

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const currRows = Array.isArray(currentDetail[rowKey]) ? currentDetail[rowKey] : [];
        const originRolls = collectOriginRollsForCategory(
            rowKey,
            periods,
            activeIndex,
            getDetail,
            apiCode,
            seedIdRef,
        );
        const blocked = collectBlockedPpeOriginIdentities(
            rowKey,
            periods,
            activeIndex,
            getDetail,
            apiCode,
        );

        const dropIfBlocked = (row: any): boolean => {
            if (blocked.keys.size === 0 && blocked.items.length === 0) return false;
            if (row?.ppeOriginKey && blocked.keys.has(String(row.ppeOriginKey))) return true;
            const name = normItem(row?.item);
            if (!name) return false;
            return blocked.items.some((b) => {
                if (b.item !== name) return false;
                if (b.date && row?.date && (row.date ?? null) !== b.date) return false;
                return true;
            });
        };

        if (originRolls.length === 0) {
            // Still drop Cost c/f=0 / suppressed lineages left on this Tab after prior save
            if (currRows.some(dropIfBlocked)) {
                changed = true;
                const kept = currRows.filter((row: any) => !dropIfBlocked(row));
                next[rowKey] = kept.length > 0
                    ? kept
                    : [{
                        id: 0,
                        date: null,
                        item: '',
                        costBf: null,
                        additions: null,
                        disposalCost: null,
                        accDepBf: null,
                    }];
            }
            continue;
        }

        if (isBlankPpeRows(currRows)) {
            changed = true;
            next[rowKey] = originRolls.map((o) => o.rolled);
            continue;
        }

        const usedKeys = new Set<string>();
        let catChanged = false;
        const updated: any[] = [];
        const activeKeyPrefix = `${activeSliceKey}#`;

        for (const row of currRows) {
            if (dropIfBlocked(row)) {
                catChanged = true;
                continue;
            }
            const match = findOriginRollForRow(row, originRolls);

            // Local row (editable origin on some Tab)
            if (!isPpeCostBfRolled(row)) {
                if (match && !match.key.startsWith(activeKeyPrefix)) {
                    // Clone of an earlier-Tab origin leaked into this Tab → one display row only
                    if (usedKeys.has(match.key)) {
                        catChanged = true;
                        continue;
                    }
                    usedKeys.add(match.key);
                    catChanged = true;
                    updated.push(applyRolledDisplayFields(row, match));
                    continue;
                }
                // This Tab's own origin (or unmatched local) — keep editable
                if (match) usedKeys.add(match.key);
                updated.push(row);
                continue;
            }

            // Already rolled display row
            if (match) {
                if (usedKeys.has(match.key)) {
                    catChanged = true;
                    continue;
                }
                usedKeys.add(match.key);
                const nextRow = applyRolledDisplayFields(row, match);
                const same =
                    nearlyEq(num(row.costBf), num(nextRow.costBf))
                    && nearlyEq(num(row.accDepBf), num(nextRow.accDepBf))
                    && row.additions == null
                    && (row.date ?? null) === nextRow.date
                    && String(row.item ?? '') === String(nextRow.item)
                    && row.ppeOriginKey === match.key;
                if (same) {
                    updated.push(row);
                } else {
                    catChanged = true;
                    updated.push(nextRow);
                }
                continue;
            }

            // Orphaned rolled row — origin deleted on an earlier Tab; drop display copy
            if (row?.ppeOriginKey) {
                catChanged = true;
                continue;
            }
            updated.push(row);
        }

        const appended = originRolls
            .filter((o) => !usedKeys.has(o.key))
            .map((o) => o.rolled);
        if (appended.length > 0 || catChanged) {
            changed = true;
            next[rowKey] = appended.length > 0 ? [...updated, ...appended] : updated;
        }
    }

    return changed ? next : null;
}

/** Prior origins blocked from appearing on {@code activeIndex} (Cost c/f=0 / suppress). */
function collectBlockedPpeOriginIdentities(
    rowKey: (typeof PPE_ROW_ARRAY_KEYS)[number],
    periods: PpePeriodSlice[],
    activeIndex: number,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): {keys: Set<string>; items: Array<{item: string; date: string | null}>} {
    const keys = new Set<string>();
    const items: Array<{item: string; date: string | null}> = [];
    for (let i = 0; i < activeIndex; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
        rows.forEach((row: any, index: number) => {
            if (!isPpeOriginRow(row) && !isPpeCostBfRolled(row)) return;
            const key = row?.ppeOriginKey
                ? String(row.ppeOriginKey)
                : (isPpeOriginRow(row) ? ppeOriginKey(slice.key, row, index) : '');
            if (!key && !normItem(row?.item)) return;
            if (!ppeOriginBlockedForActiveTab(
                rowKey, periods, i, activeIndex,
                key || ppeOriginKey(slice.key, row, index),
                row, getDetail, apiCode,
            )) {
                return;
            }
            if (key) keys.add(key);
            const name = normItem(row?.item);
            if (name) items.push({item: name, date: row?.date ?? null});
        });
    }
    return {keys, items};
}

/**
 * MULTI save: re-apply forward roll on every non-first Period Tab so later YOA
 * buckets (e.g. computerHardSoft seeded from prior origin) are present even when
 * the user never reopened that Tab after editing an earlier year.
 */
export function syncPpeForwardRollAcrossPeriods(
    periods: PpePeriodSlice[] | null | undefined,
    apiCode: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
): void {
    if (!apiCode || !Array.isArray(periods) || periods.length < 2) return;
    for (let i = 1; i < periods.length; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const draftKey = `${apiCode}__${slice.key}`;
        const current = getDetail(draftKey);
        if (!current || typeof current !== 'object') continue;
        const rolled = applyPpeForwardRollFromPriors(
            current,
            periods,
            slice.key,
            getDetail,
            apiCode,
        );
        if (!rolled) continue;
        try {
            setDetail(
                draftKey,
                typeof structuredClone === 'function'
                    ? structuredClone(rolled)
                    : JSON.parse(JSON.stringify(rolled)),
            );
        } catch {
            setDetail(draftKey, JSON.parse(JSON.stringify(rolled)));
        }
    }
}

/**
 * MULTI: after deleting an origin row on Tab N, remove matching rows from later
 * Period Tab drafts (N+1…) in the same SubTable.
 * Same category + same item (or same ppeOriginKey) cascade is intentional.
 * When {@code sourceRowKey} is set, other SubTables are left untouched.
 * <p>
 * Persisted later-year rows lose {@code ppeCostBfIsCostBf}/{@code ppeOriginKey} on
 * save — match by item name (and date when both dated), same pattern as Rental.
 */
export function removePpeOriginFromLaterTabs(
    periods: PpePeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    apiCode: string,
    originKey: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
    /** Also match by item (+ date) when ppeOriginKey is missing on older drafts */
    deletedRow?: {date?: string | null; item?: string | null} | null,
    /** SubTable array key — same-category cascade only */
    sourceRowKey?: string | null,
): void {
    if (!originKey || !apiCode || !Array.isArray(periods) || !activeSliceKey) return;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex < 0 || activeIndex >= periods.length - 1) return;

    const delDate = deletedRow?.date ?? null;
    const delItem = normItem(deletedRow?.item);
    // Same-category cascade (normal MULTI). Without sourceRowKey, fall back to all keys
    // but item match still needs a non-empty name — prefer callers always pass sourceRowKey.
    const scopedKeys = sourceRowKey
        && (PPE_ROW_ARRAY_KEYS as readonly string[]).includes(sourceRowKey)
        ? [sourceRowKey as (typeof PPE_ROW_ARRAY_KEYS)[number]]
        : PPE_ROW_ARRAY_KEYS;

    for (let i = activeIndex + 1; i < periods.length; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const draftKey = `${apiCode}__${slice.key}`;
        const detail = getDetail(draftKey);
        if (!detail || typeof detail !== 'object') continue;

        let changed = false;
        const next: any = {...detail};
        for (const rowKey of scopedKeys) {
            const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : null;
            if (!rows || rows.length === 0) continue;
            const filtered = rows.filter((row: any) => {
                if (row?.ppeOriginKey && String(row.ppeOriginKey) === originKey) return false;
                // Same SubTable + same item name (markers optional after save/reload)
                if (delItem && normItem(row.item) === delItem) {
                    // When both dated and dates differ, keep (distinct assets)
                    if (delDate && row.date && (row.date ?? null) !== delDate) return true;
                    return false;
                }
                return true;
            });
            if (filtered.length !== rows.length) {
                changed = true;
                next[rowKey] = filtered.length > 0
                    ? filtered
                    : [{
                        id: 0,
                        date: null,
                        item: '',
                        costBf: null,
                        additions: null,
                        disposalCost: null,
                        accDepBf: null,
                    }];
            }
        }
        if (changed) {
            try {
                setDetail(draftKey, typeof structuredClone === 'function'
                    ? structuredClone(next)
                    : JSON.parse(JSON.stringify(next)));
            } catch {
                setDetail(draftKey, JSON.parse(JSON.stringify(next)));
            }
        }
    }
}

/**
 * MULTI: walk every Tab and remove later-Tab copies of any row whose Cost c/f is 0
 * (or sticky {@code ppeSuppressLaterRoll}). Soft-delete until parent Save (same as row delete).
 */
export function prunePpeZeroCostCfFromLaterTabs(
    periods: PpePeriodSlice[] | null | undefined,
    apiCode: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
): void {
    if (!apiCode || !Array.isArray(periods) || periods.length < 2) return;
    for (let i = 0; i < periods.length - 1; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        for (const rowKey of PPE_ROW_ARRAY_KEYS) {
            const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
            if (rows.length === 0) continue;
            const rate = ppeEffectiveDepRateForRowKey(detail, rowKey);
            rows.forEach((row: any, index: number) => {
                if (!String(row?.item ?? '').trim()) return;
                if (!ppeRowBlocksLaterRoll(row, rate, slice.periodFrom, slice.periodTo)) return;
                const originKey = row?.ppeOriginKey
                    ? String(row.ppeOriginKey)
                    : buildPpeOriginKey(slice.key, row, index);
                removePpeOriginFromLaterTabs(
                    periods,
                    slice.key,
                    apiCode,
                    originKey,
                    getDetail,
                    setDetail,
                    row,
                    rowKey,
                );
            });
        }
    }
}

/**
 * @deprecated Prefer applyPpeForwardRollFromPriors (multi-origin, date-gated).
 * Overlay / seed from a single prior detail (legacy nearest-prior path).
 */
export function applyPpeBfFromPriorCf(
    currentDetail: any,
    priorDetail: any,
    ctx: PpePriorCfContext,
    periodSlicesToApply?: Array<{periodFrom?: string; periodTo?: string}> | null,
): any | null {
    if (!currentDetail || !priorDetail) return null;
    const rates = ctx.ppeRate || priorDetail.ppeRate || currentDetail.ppeRate || {};
    const slices: Array<{periodFrom?: string; periodTo?: string}> =
        Array.isArray(periodSlicesToApply) && periodSlicesToApply.length > 0
            ? periodSlicesToApply.map((p) => ({
                periodFrom: p.periodFrom ?? undefined,
                periodTo: p.periodTo ?? undefined,
            }))
            : [{periodFrom: ctx.periodFrom ?? undefined, periodTo: ctx.periodTo ?? undefined}];

    let changed = false;
    const next: any = {...currentDetail};
    let seedId = -1;

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const currRows = Array.isArray(currentDetail[rowKey]) ? currentDetail[rowKey] : [];
        const priorRows = Array.isArray(priorDetail[rowKey]) ? priorDetail[rowKey] : null;
        if (!priorRows || priorRows.length === 0) continue;

        const rateKey = PPE_RATE_BY_ROW_KEY[rowKey];
        const rawRate = Number(rates?.[rateKey] ?? 0) || 0;
        const depRate = ratePercentToFraction(rawRate);
        const irdFrac =
            rowKey === 'equipmentRow'
                ? ratePercentToFraction(Number(rates?.equipmentIrdRate ?? rawRate) || 0)
                : depRate;
        const effectiveRate = rowKey === 'equipmentRow' ? irdFrac : depRate;

        const eligiblePriors = priorRows.filter(isPpeOriginRow);
        if (eligiblePriors.length === 0) continue;

        if (isBlankPpeRows(currRows)) {
            const seeded = eligiblePriors.map((prior: any, index: number) =>
                rollPpeRowAcrossPeriods(
                    prior,
                    effectiveRate,
                    slices,
                    seedId--,
                    ppeOriginKey('prior', prior, index),
                ));
            changed = true;
            next[rowKey] = seeded;
            continue;
        }

        const usedPriorIndexes = new Set<number>();
        const updated = currRows.map((row: any, index: number) => {
            if (!isPpeCostBfRolled(row)) return row;
            const prior = findPriorRow(row, eligiblePriors, index);
            if (!prior) return row;
            const priorIdx = eligiblePriors.indexOf(prior);
            if (priorIdx >= 0) usedPriorIndexes.add(priorIdx);
            const key = ppeOriginKey('prior', prior, priorIdx);
            const rolled = rollPpeRowAcrossPeriods(prior, effectiveRate, slices, row.id ?? seedId--, key);
            changed = true;
            const nextRow = {
                ...row,
                date: rolled.date ?? null,
                item: rolled.item ?? '',
                costBf: rolled.costBf,
                additions: null,
                accDepBf: rolled.accDepBf,
                disposalCost: row.disposalCost ?? null,
                ppeCostBfIsCostBf: true,
                ppeOriginKey: key,
            };
            delete (nextRow as any).ppeCostBfIsPriorNbv;
            return nextRow;
        });

        const appended: any[] = [];
        eligiblePriors.forEach((prior: any, priorIdx: number) => {
            if (usedPriorIndexes.has(priorIdx)) return;
            appended.push(
                rollPpeRowAcrossPeriods(
                    prior,
                    effectiveRate,
                    slices,
                    seedId--,
                    ppeOriginKey('prior', prior, priorIdx),
                ),
            );
        });
        if (appended.length > 0 || changed) {
            changed = true;
            next[rowKey] = [...updated, ...appended];
        }
    }

    return changed ? next : null;
}

function isBlankIaRow(row: any): boolean {
    if (!row || typeof row !== 'object') return true;
    const hasSavedId = row.id != null && Number(row.id) > 0;
    if (hasSavedId) return false;
    const hasCost = row.costBf != null && Number(row.costBf) !== 0;
    const hasAdd = row.addition != null && Number(row.addition) !== 0;
    const hasCostDisp = row.costDisposal != null && Number(row.costDisposal) !== 0;
    const hasAmorBf = row.accAmorBf != null && Number(row.accAmorBf) !== 0;
    const hasAmorChg = row.amorChg != null && Number(row.amorChg) !== 0;
    const hasAccDisp = row.accDisposal != null && Number(row.accDisposal) !== 0;
    return !hasCost && !hasAdd && !hasCostDisp && !hasAmorBf && !hasAmorChg && !hasAccDisp;
}

function isBlankIaRows(rows: any[] | null | undefined): boolean {
    if (!Array.isArray(rows) || rows.length === 0) return true;
    return rows.every(isBlankIaRow);
}

function iaPriorHasCarryContent(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (String(row.item || '').trim()) return true;
    const derived = computeIaClosingCf(row);
    return derived.costCf !== 0 || derived.accAmorCf !== 0;
}

/**
 * MULTI IA/ITL: Cost c/f === 0 (or sticky {@code iaSuppressLaterRoll}) → do not
 * carry this item onto later Period Tabs. Q4B: suppress sticks after Cost c/f recovers.
 */
export function iaRowBlocksLaterRoll(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (row.iaSuppressLaterRoll) return true;
    if (!String(row.item ?? '').trim()) return false;
    return computeIaClosingCf(row).costCf === 0;
}

function emptyIaPlaceholderRow(): any {
    return {
        id: 0,
        item: '',
        category: null,
        costBf: null,
        addition: null,
        costDisposal: null,
        accAmorBf: null,
        amorChg: null,
        accDisposal: null,
    };
}

/** Row-array keys to scan on one IA / ITL detail (optional section scope for ITL). */
function iaDetailRowKeys(
    detail: any,
    sourceRowKey?: string | null,
): string[] {
    if (sourceRowKey) return [sourceRowKey];
    if (!detail || typeof detail !== 'object') return ['intangibleRows'];
    const hasItl = ITL_ROW_ARRAY_KEYS.some((k) => Array.isArray(detail[k]));
    if (hasItl) return [...ITL_ROW_ARRAY_KEYS];
    if (Array.isArray(detail.intangibleRows)) return ['intangibleRows'];
    if (Array.isArray(detail.intangibleRow)) return ['intangibleRow'];
    return ['intangibleRows'];
}

/** Fingerprint of IA / ITL rows — drives re-roll when current Tab is overwritten by empty factory. */
export function iaDetailRowsFingerprint(detail: any): string {
    if (!detail || typeof detail !== 'object') return '';
    const hasItlSections = ITL_ROW_ARRAY_KEYS.some((k) => Array.isArray(detail[k]));
    if (hasItlSections) {
        const payload: Record<string, unknown> = {};
        for (const k of ITL_ROW_ARRAY_KEYS) {
            payload[k] = detail[k];
        }
        return JSON.stringify(payload);
    }
    const rows = Array.isArray(detail.intangibleRows)
        ? detail.intangibleRows
        : (Array.isArray(detail.intangibleRow) ? detail.intangibleRow : null);
    return JSON.stringify(rows ?? null);
}

function seedIaRowFromPrior(prior: any, id: number): any {
    const derived = computeIaClosingCf(prior);
    return {
        id,
        item: prior?.item ?? '',
        category: prior?.category ?? null,
        // Align with PPE MULTI: costBf ← prior costBf/addition (non-zero end); not costCf
        costBf: pickIaCostBfFromPriorCostOrAddition(prior),
        addition: null,
        costDisposal: null,
        accAmorBf: derived.accAmorCf,
        amorChg: null,
        accDisposal: null,
        /** Item name originated on an earlier Period Tab — display-only on this Tab */
        iaItemFromPrior: true,
    };
}

/**
 * MULTI IA / ITL Period Tabs (display/session only; SINGLE unchanged #20):
 *   costBf    ← prior costBf/addition (non-zero end) — same rule as PPE
 *   accAmorBf ← prior Acc amor c/f
 *   addition  cleared on seed; overlay keeps this-Tab movements unless blank seed
 * - Blank / factory Tab → seed rows from prior (movements cleared; item locked)
 * - Existing rows → overlay matched item/index (item locked); append unmatched prior items
 * - Local-only rows (no prior match) keep editable item names
 */
export function applyIaBfFromPriorCf(currentDetail: any, priorDetail: any): any | null {
    if (!currentDetail || !priorDetail) return null;

    const hasItlSections = ITL_ROW_ARRAY_KEYS.some((k) =>
        Array.isArray(currentDetail[k]) || Array.isArray(priorDetail[k]),
    );
    if (hasItlSections) {
        let anyChanged = false;
        const next = {...currentDetail};
        for (const sectionKey of ITL_ROW_ARRAY_KEYS) {
            const rolled = applyIaBfFromPriorCfSingle(
                {
                    ...currentDetail,
                    intangibleRows: Array.isArray(currentDetail[sectionKey])
                        ? currentDetail[sectionKey]
                        : [],
                },
                {
                    ...priorDetail,
                    intangibleRows: Array.isArray(priorDetail[sectionKey])
                        ? priorDetail[sectionKey]
                        : [],
                },
            );
            if (rolled && Array.isArray(rolled.intangibleRows)) {
                next[sectionKey] = rolled.intangibleRows;
                anyChanged = true;
            }
        }
        return anyChanged ? next : null;
    }

    return applyIaBfFromPriorCfSingle(currentDetail, priorDetail);
}

function applyIaBfFromPriorCfSingle(currentDetail: any, priorDetail: any): any | null {
    if (!currentDetail || !priorDetail) return null;
    const currRowsRaw = Array.isArray(currentDetail.intangibleRows)
        ? currentDetail.intangibleRows
        : (Array.isArray(currentDetail.intangibleRow) ? currentDetail.intangibleRow : null);
    const priorRowsRaw = Array.isArray(priorDetail.intangibleRows)
        ? priorDetail.intangibleRows
        : (Array.isArray(priorDetail.intangibleRow) ? priorDetail.intangibleRow : null);
    if (!priorRowsRaw || priorRowsRaw.length === 0) return null;

    // Cost c/f === 0 / sticky suppress → never seed or overlay onto later Tabs
    const eligiblePriors = priorRowsRaw.filter(
        (r: any) => iaPriorHasCarryContent(r) && !iaRowBlocksLaterRoll(r),
    );
    const blockedNames = new Set(
        priorRowsRaw
            .filter(iaRowBlocksLaterRoll)
            .map((r: any) => normItem(r?.item))
            .filter(Boolean),
    );
    const eligibleNames = new Set(
        eligiblePriors.map((r: any) => normItem(r?.item)).filter(Boolean),
    );

    const rowKey = Array.isArray(currentDetail.intangibleRows)
        ? 'intangibleRows'
        : (Array.isArray(currentDetail.intangibleRow) ? 'intangibleRow' : 'intangibleRows');
    const currRows = Array.isArray(currRowsRaw) ? currRowsRaw : [];

    // Drop later-Tab copies of blocked / orphaned prior items (same as PPE costCf=0)
    const keptCurr = currRows.filter((row: any) => {
        const name = normItem(row?.item);
        if (name && blockedNames.has(name)) return false;
        if (row?.iaItemFromPrior && name && !eligibleNames.has(name)) return false;
        return true;
    });
    const droppedBlocked = keptCurr.length !== currRows.length;

    if (eligiblePriors.length === 0) {
        if (!droppedBlocked) return null;
        return {
            ...currentDetail,
            [rowKey]: keptCurr.length > 0 ? keptCurr : [emptyIaPlaceholderRow()],
        };
    }

    let seedId = -1;
    if (isBlankIaRows(keptCurr)) {
        const seeded = eligiblePriors.map((prior: any) => seedIaRowFromPrior(prior, seedId--));
        return {...currentDetail, [rowKey]: seeded};
    }

    let changed = droppedBlocked;
    const usedPriorIndexes = new Set<number>();
    const updated = keptCurr.map((row: any, index: number) => {
        const prior = findPriorRow(row, eligiblePriors, index);
        if (!prior) return row;
        const priorIdx = eligiblePriors.indexOf(prior);
        if (priorIdx >= 0) usedPriorIndexes.add(priorIdx);
        const derived = computeIaClosingCf(prior);
        const costBf = pickIaCostBfFromPriorCostOrAddition(prior);
        const accAmorBf = derived.accAmorCf;
        const nextItem = prior.item ?? row.item;
        const sameAmounts =
            nearlyEq(num(row.costBf), num(costBf))
            && nearlyEq(num(row.accAmorBf), accAmorBf);
        const sameItem = String(row.item ?? '') === String(nextItem ?? '');
        if (sameAmounts && sameItem && row.iaItemFromPrior === true) {
            return row;
        }
        changed = true;
        return {
            ...row,
            item: nextItem,
            costBf,
            accAmorBf,
            iaItemFromPrior: true,
        };
    });

    const appended: any[] = [];
    eligiblePriors.forEach((prior: any, priorIdx: number) => {
        if (usedPriorIndexes.has(priorIdx)) return;
        appended.push(seedIaRowFromPrior(prior, seedId--));
    });
    if (appended.length > 0) changed = true;

    if (!changed) return null;
    return {
        ...currentDetail,
        [rowKey]: appended.length > 0 ? [...updated, ...appended] : updated,
    };
}

/**
 * MULTI IA/ITL: after Cost c/f → 0 (or delete) on Tab N, remove same-item rows
 * from later Period Tab drafts (N+1…). Optional {@code sourceRowKey} scopes ITL section.
 */
export function removeIaItemFromLaterTabs(
    periods: PpePeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    apiCode: string,
    itemName: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
    sourceRowKey?: string | null,
): void {
    const delItem = normItem(itemName);
    if (!delItem || !apiCode || !Array.isArray(periods) || !activeSliceKey) return;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex < 0 || activeIndex >= periods.length - 1) return;

    for (let i = activeIndex + 1; i < periods.length; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const draftKey = `${apiCode}__${slice.key}`;
        const detail = getDetail(draftKey);
        if (!detail || typeof detail !== 'object') continue;

        let changed = false;
        const next: any = {...detail};
        for (const rowKey of iaDetailRowKeys(detail, sourceRowKey)) {
            const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : null;
            if (!rows || rows.length === 0) continue;
            const filtered = rows.filter((row: any) => normItem(row?.item) !== delItem);
            if (filtered.length !== rows.length) {
                changed = true;
                next[rowKey] = filtered.length > 0 ? filtered : [emptyIaPlaceholderRow()];
            }
        }
        if (!changed) continue;
        try {
            setDetail(
                draftKey,
                typeof structuredClone === 'function'
                    ? structuredClone(next)
                    : JSON.parse(JSON.stringify(next)),
            );
        } catch {
            setDetail(draftKey, JSON.parse(JSON.stringify(next)));
        }
    }
}

/**
 * MULTI IA/ITL: walk every Tab and remove later-Tab copies of rows whose Cost c/f
 * is 0 (or sticky {@code iaSuppressLaterRoll}). Soft-delete until parent Save.
 */
export function pruneIaZeroCostCfFromLaterTabs(
    periods: PpePeriodSlice[] | null | undefined,
    apiCode: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
): void {
    if (!apiCode || !Array.isArray(periods) || periods.length < 2) return;
    for (let i = 0; i < periods.length - 1; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        if (!detail || typeof detail !== 'object') continue;
        for (const rowKey of iaDetailRowKeys(detail)) {
            const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
            for (const row of rows) {
                if (!iaRowBlocksLaterRoll(row)) continue;
                removeIaItemFromLaterTabs(
                    periods,
                    slice.key,
                    apiCode,
                    String(row.item ?? ''),
                    getDetail,
                    setDetail,
                    rowKey,
                );
            }
        }
    }
}

export function resolvePriorPeriodSlice(
    periods: Array<{
        key: string;
        periodFrom?: string;
        periodTo?: string;
        yearOfAssessment?: string | null;
    }> | null | undefined,
    activeSliceKey: string | null | undefined,
): {
    priorKey: string;
    periodFrom?: string;
    periodTo?: string;
    yearOfAssessment?: string | null;
    index: number;
} | null {
    if (!Array.isArray(periods) || periods.length < 2 || !activeSliceKey) return null;
    const index = periods.findIndex((p) => p.key === activeSliceKey);
    if (index <= 0) return null;
    const prior = periods[index - 1];
    if (!prior?.key) return null;
    return {
        priorKey: prior.key,
        periodFrom: prior.periodFrom,
        periodTo: prior.periodTo,
        yearOfAssessment: prior.yearOfAssessment ?? null,
        index,
    };
}

/** Current-year Final = perClient + adjustment(s) — same as Common / Commission WP display. */
export function computeWpRowCurrentFinal(row: any): number {
    let adj = 0;
    if (Array.isArray(row?.adjustments) && row.adjustments.length > 0) {
        adj = row.adjustments.reduce((s: number, d: any) => s + (Number(d?.adjustment) || 0), 0);
    } else {
        adj = Number(row?.adjustment) || 0;
    }
    return num(row?.perClient) + adj;
}

function isEmptyLastYearFinal(v: unknown): boolean {
    if (v == null || v === '') return true;
    const n = Number(v);
    return !Number.isFinite(n) || n === 0;
}

/**
 * MULTI non-first Tab: fill lastYearFinal from prior slice WP Final (perClient + adj).
 * Match rows by nameKey then index (same as PPE b/f).
 */
export function applyCommonLastYearFinalFromPrior(
    currentDetail: any,
    priorDetail: any,
    opts: {
        rowsKey: string;
        nameKey: string;
        /** When true (default), only overwrite empty/0 lastYearFinal. */
        onlyIfEmpty?: boolean;
    },
): any | null {
    if (!currentDetail || !priorDetail) return null;
    const rowsKey = opts.rowsKey;
    const nameKey = opts.nameKey;
    const onlyIfEmpty = opts.onlyIfEmpty !== false;
    const currRows: any[] = Array.isArray(currentDetail[rowsKey]) ? currentDetail[rowsKey] : [];
    const priorRows: any[] = Array.isArray(priorDetail[rowsKey]) ? priorDetail[rowsKey] : [];
    if (currRows.length === 0 || priorRows.length === 0) return null;

    const priorAsNamed = priorRows.map((r) => ({...r, item: r?.[nameKey]}));

    let changed = false;
    const updated = currRows.map((row, i) => {
        if (onlyIfEmpty && !isEmptyLastYearFinal(row?.lastYearFinal)) return row;
        const matched = findPriorRow({item: row?.[nameKey]}, priorAsNamed, i);
        if (!matched) return row;
        const ly = computeWpRowCurrentFinal(matched);
        if (Number(row?.lastYearFinal) === ly) return row;
        changed = true;
        return {...row, lastYearFinal: ly};
    });

    if (!changed) return null;
    return {...currentDetail, [rowsKey]: updated};
}

/**
 * CommonInvestment (ISUB / IASS / IOT): MULTI roll-forward.
 * Prefers intangibleRows (IA costBf/accAmorBf ← prior C/F); falls back to legacy lastYearFinal.
 */
export function applyCommonInvestmentLastYearFinalFromPrior(
    currentDetail: any,
    priorDetail: any,
    opts?: {onlyIfEmpty?: boolean},
): any | null {
    if (!currentDetail || !priorDetail) return null;
    const hasIntangible = Array.isArray(currentDetail.intangibleRows)
        || Array.isArray(priorDetail.intangibleRows);
    if (hasIntangible) {
        return applyIaBfFromPriorCf(currentDetail, priorDetail);
    }
    const onlyIfEmpty = opts?.onlyIfEmpty === true;
    let next: any = currentDetail;
    let changed = false;
    for (const rowsKey of ['costRow', 'impairmentLossRow'] as const) {
        const applied = applyCommonLastYearFinalFromPrior(next, priorDetail, {
            rowsKey,
            nameKey: 'item',
            onlyIfEmpty,
        });
        if (applied) {
            next = applied;
            changed = true;
        }
    }
    return changed ? next : null;
}

/** Fingerprint of CommonInvestment amount rows for prior→lastYearFinal re-seed signatures. */
export function commonInvestmentDetailRowsFingerprint(detail: any): string {
    if (Array.isArray(detail?.intangibleRows)) {
        return iaDetailRowsFingerprint(detail);
    }
    const mapRows = (rows: any[] | undefined) =>
        (Array.isArray(rows) ? rows : []).map((r) => [
            String(r?.item ?? ''),
            computeWpRowCurrentFinal(r),
            Number(r?.lastYearFinal) || 0,
        ]);
    return JSON.stringify({
        cost: mapRows(detail?.costRow),
        impair: mapRows(detail?.impairmentLossRow),
    });
}

const UNLISTED_SHARES_AT_COST = 'Unlisted shares, at cost';

function extractCommonInvestmentAmountRows(detail: any): {cost: any[]; impair: any[]} {
    if (!detail || typeof detail !== 'object') return {cost: [], impair: []};
    const costRaw: any[] = Array.isArray(detail.costRow) ? detail.costRow : [];
    const cost = costRaw.filter((row: any) => String(row?.item || '').trim() !== UNLISTED_SHARES_AT_COST);
    const impair: any[] = Array.isArray(detail.impairmentLossRow) ? detail.impairmentLossRow : [];
    return {cost, impair};
}

function sumInvestmentRowsCurrentFinal(rows: any[]): number {
    let total = 0;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const autoAdj = row.autoAdj != null ? num(row.autoAdj) : 0;
        total += computeWpRowCurrentFinal(row) + autoAdj;
    }
    return total;
}

function sumInvestmentRowsLastYearFinal(rows: any[]): number {
    let total = 0;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        total += num(row.lastYearFinal);
    }
    return total;
}

/**
 * MULTI WpPerCheck: Net book value Final for one CommonInvestment Period Tab.
 * Prefers intangibleRows (Σ nbvCf); falls back to legacy cost+impairment rows.
 */
export function sumCommonInvestmentDetailNbvFinal(detail: any): number {
    if (Array.isArray(detail?.intangibleRows) && detail.intangibleRows.length > 0) {
        return sumIaDetailGrandNbvCf(detail);
    }
    const {cost, impair} = extractCommonInvestmentAmountRows(detail);
    return sumInvestmentRowsCurrentFinal(cost) + sumInvestmentRowsCurrentFinal(impair);
}

/**
 * MULTI WpPerCheck: Net book value LastYear Final for one CommonInvestment Period Tab.
 * Prefers intangibleRows (Σ nbvBf); falls back to legacy lastYearFinal sums.
 */
export function sumCommonInvestmentDetailNbvLastYearFinal(detail: any): number {
    if (Array.isArray(detail?.intangibleRows) && detail.intangibleRows.length > 0) {
        return sumIaDetailGrandNbvBf(detail);
    }
    const {cost, impair} = extractCommonInvestmentAmountRows(detail);
    return sumInvestmentRowsLastYearFinal(cost) + sumInvestmentRowsLastYearFinal(impair);
}

// ── Issued Capital (BS-EQ-IC) shareholders: prior Per Client → Current + lastYearFinal ──

function isBackendShareholderId(id: unknown): boolean {
    return typeof id === 'number' && Number.isFinite(id) && id > 0;
}

function shareholderNameKey(row: any): string {
    return normItem(row?.name ?? row?.shareholderName);
}

/** Align with ShareholdersTable.getEffectiveCurrentSharesPrice (no UI imports). #20 SINGLE fallbacks unchanged. */
export function computeIssuedCapitalEffectiveCurrentSharesPrice(sh: any): number {
    if (sh?.totalPriceOverride !== null && sh?.totalPriceOverride !== undefined) {
        return num(sh.totalPriceOverride);
    }
    const computed = num(sh?.parValue) * num(sh?.currentShares) * (num(sh?.exchangeRate ?? 1) || 1);
    if (computed !== 0) return computed;
    const backendEffective = num(sh?.effectiveFinalPrice);
    if (backendEffective !== 0) return backendEffective;
    return num(sh?.currentSharesPrice);
}

/** Per Client shares = Current + Transfer + Allotment. */
export function computeIssuedCapitalPerClientShares(sh: any): number {
    return num(sh?.currentShares) + num(sh?.transferShare) + num(sh?.allotmentShare);
}

/** Per Client price = Current price + Transfer price + Allotment price. */
export function computeIssuedCapitalPerClientPrice(sh: any): number {
    return computeIssuedCapitalEffectiveCurrentSharesPrice(sh)
        + num(sh?.transferSharesPrice)
        + num(sh?.allotmentSharesPrice);
}

/** MULTI WpPerCheck: Final Shares Prices total for one IC Period Tab draft. */
export function sumIssuedCapitalDetailFinalSharesPrice(detail: any): number {
    const rows: any[] = Array.isArray(detail?.shareholders) ? detail.shareholders : [];
    return rows.reduce((sum, sh) => sum + computeIssuedCapitalPerClientPrice(sh), 0);
}

/** MULTI WpPerCheck: Last Year Final Shares Price total for one IC Period Tab draft. */
export function sumIssuedCapitalDetailLastYearFinalSharesPrice(detail: any): number {
    const rows: any[] = Array.isArray(detail?.shareholders) ? detail.shareholders : [];
    return rows.reduce((sum, sh) => sum + num(sh?.lastYearFinalSharesPrice), 0);
}

function findPriorShareholder(curr: any, priorRows: any[], currIndex: number): any | null {
    const id = curr?.id ?? curr?.shareholderId;
    if (isBackendShareholderId(id)) {
        const byId = priorRows.find((r) => {
            const rid = r?.id ?? r?.shareholderId;
            return isBackendShareholderId(rid) && rid === id;
        });
        if (byId) return byId;
    }
    const name = shareholderNameKey(curr);
    if (name) {
        const byName = priorRows.find((r) => shareholderNameKey(r) === name);
        if (byName) return byName;
    }
    return priorRows[currIndex] ?? null;
}

/**
 * MULTI: true when `sh` already appears in earlier-Tab rows (backend id or normalized name).
 * Used to lock shareholder identity edit/delete on Tabs after the origin Tab.
 * Does not use index fallback (unlike roll matching).
 */
export function isIssuedCapitalShareholderInList(sh: any, rows: any[] | null | undefined): boolean {
    if (!sh || !Array.isArray(rows) || rows.length === 0) return false;
    const id = sh?.id ?? sh?.shareholderId;
    if (isBackendShareholderId(id)) {
        const byId = rows.some((r) => {
            const rid = r?.id ?? r?.shareholderId;
            return isBackendShareholderId(rid) && rid === id;
        });
        if (byId) return true;
    }
    const name = shareholderNameKey(sh);
    if (!name) return false;
    return rows.some((r) => shareholderNameKey(r) === name);
}

function isEmptyIssuedCapitalCurrentAndLastYear(row: any): boolean {
    const currentEmpty = num(row?.currentShares) === 0
        && nearlyEq(computeIssuedCapitalEffectiveCurrentSharesPrice(row), 0);
    const ly = row?.lastYearFinalSharesPrice;
    const lyEmpty = ly == null || ly === '' || nearlyEq(num(ly), 0);
    return currentEmpty && lyEmpty;
}

/**
 * MULTI: true when prior Per Client shares != 0 — roll forward to later Tabs.
 * Fully exited holders (Per Client shares === 0 after transfer) are hidden on later YOAs;
 * they remain visible on the process YOA (with Transfer/Allotment columns).
 * Note: Current shares === 0 with non-zero Transfer/Allotment still rolls (Per Client != 0).
 * SINGLE / callers that do not opt in must not use this filter (#20).
 */
export function shouldRollIssuedCapitalShareholderForward(prior: any): boolean {
    return computeIssuedCapitalPerClientShares(prior) !== 0;
}

function openIssuedCapitalCurrentFromPerClient(prior: any, base?: any): any {
    const perShares = computeIssuedCapitalPerClientShares(prior);
    const perPrice = computeIssuedCapitalPerClientPrice(prior);
    return {
        ...(base || prior),
        id: base?.id ?? prior?.id ?? null,
        name: base?.name ?? prior?.name ?? prior?.shareholderName ?? '',
        type: base?.type ?? prior?.type ?? 'INDIVIDUAL',
        currency: base?.currency ?? prior?.currency ?? 'HKD',
        exchangeRate: base?.exchangeRate ?? prior?.exchangeRate ?? 1,
        parValue: base?.parValue ?? prior?.parValue ?? 1,
        currentShares: perShares,
        currentSharesPrice: perPrice,
        lastYearFinalSharesPrice: perPrice,
        // Lock Current price to prior Per Client total (may differ from par×shares×rate)
        totalPriceOverride: perPrice,
        // Preserve this-Tab movements when re-rolling; new Tab clears them before roll
        transferShare: base?.transferShare ?? null,
        transferSharesPrice: base?.transferSharesPrice ?? null,
        transferTransactionIds: base?.transferTransactionIds ?? null,
        allotmentShare: base?.allotmentShare ?? null,
        allotmentSharesPrice: base?.allotmentSharesPrice ?? null,
        allotmentTransactionIds: base?.allotmentTransactionIds ?? null,
        gender: base?.gender ?? prior?.gender ?? null,
        entryDate: base?.entryDate ?? prior?.entryDate ?? null,
        originYoa: base?.originYoa ?? prior?.originYoa ?? null,
        preEngagement: base?.preEngagement ?? prior?.preEngagement ?? null,
        companyId: base?.companyId ?? prior?.companyId ?? null,
        annualId: base?.annualId ?? prior?.annualId ?? null,
    };
}

function hasIssuedCapitalLocalProcess(row: any): boolean {
    return num(row?.transferShare) !== 0
        || num(row?.allotmentShare) !== 0
        || (Array.isArray(row?.transferTransactionIds) && row.transferTransactionIds.length > 0)
        || (Array.isArray(row?.allotmentTransactionIds) && row.allotmentTransactionIds.length > 0);
}

/**
 * IssuedCapital ShareholdersTable (MULTI non-first Tab):
 * prior Per Client → this Tab Current + lastYearFinalSharesPrice (= Current price).
 * Pass `{omitZeroPerClient: true}` for MULTI Period Tabs (hide fully exited on later YOAs).
 * Default omitZeroPerClient=false preserves legacy roll-all behavior (#20 SINGLE-safe).
 * Also appends prior shareholders missing from current (e.g. allotment/CREATE on prior Tab).
 */
export function applyIssuedCapitalShareholderRollFromPrior(
    currentDetail: any,
    priorDetail: any,
    opts?: {onlyIfEmpty?: boolean; omitZeroPerClient?: boolean},
): any | null {
    if (!currentDetail || !priorDetail) return null;
    const onlyIfEmpty = opts?.onlyIfEmpty === true;
    const omitZeroPerClient = opts?.omitZeroPerClient === true;
    const currRows: any[] = Array.isArray(currentDetail.shareholders) ? currentDetail.shareholders : [];
    const priorRows: any[] = Array.isArray(priorDetail.shareholders) ? priorDetail.shareholders : [];
    if (priorRows.length === 0) return null;

    const rollablePrior = omitZeroPerClient
        ? priorRows.filter(shouldRollIssuedCapitalShareholderForward)
        : priorRows;

    // Empty current table: seed from prior Per Client
    if (currRows.length === 0) {
        if (rollablePrior.length === 0) {
            return omitZeroPerClient ? {...currentDetail, shareholders: []} : null;
        }
        const seeded = rollablePrior.map((p) => openIssuedCapitalCurrentFromPerClient(p));
        return {...currentDetail, shareholders: seeded};
    }

    let changed = false;
    const updated: any[] = [];
    currRows.forEach((row, i) => {
        const matched = findPriorShareholder(row, priorRows, i);
        if (
            omitZeroPerClient
            && matched
            && !shouldRollIssuedCapitalShareholderForward(matched)
            && !hasIssuedCapitalLocalProcess(row)
        ) {
            // Prior ended at 0 shares — drop from later Tab unless this Tab has local process
            changed = true;
            return;
        }
        if (onlyIfEmpty && !isEmptyIssuedCapitalCurrentAndLastYear(row)) {
            updated.push(row);
            return;
        }
        if (!matched) {
            updated.push(row);
            return;
        }

        const perShares = computeIssuedCapitalPerClientShares(matched);
        const perPrice = computeIssuedCapitalPerClientPrice(matched);
        const currPrice = computeIssuedCapitalEffectiveCurrentSharesPrice(row);
        if (
            num(row?.currentShares) === perShares
            && nearlyEq(currPrice, perPrice)
            && nearlyEq(num(row?.lastYearFinalSharesPrice), perPrice)
        ) {
            updated.push(row);
            return;
        }

        changed = true;
        updated.push(openIssuedCapitalCurrentFromPerClient(matched, row));
    });

    // Forward-display: prior Tab new shareholders (allotment / CREATE / transfer in) appear on later Tabs
    const presentKeys = new Set<string>();
    for (const row of updated) {
        const id = row?.id ?? row?.shareholderId;
        if (isBackendShareholderId(id)) {
            presentKeys.add(`id:${id}`);
        }
        const name = shareholderNameKey(row);
        if (name) {
            presentKeys.add(`name:${name}`);
        }
    }
    const appended: any[] = [];
    for (const p of rollablePrior) {
        const id = p?.id ?? p?.shareholderId;
        const name = shareholderNameKey(p);
        const hasId = isBackendShareholderId(id) && presentKeys.has(`id:${id}`);
        const hasName = !!name && presentKeys.has(`name:${name}`);
        if (hasId || hasName) {
            continue;
        }
        changed = true;
        appended.push(openIssuedCapitalCurrentFromPerClient(p));
    }

    if (!changed && updated.length === currRows.length && appended.length === 0) return null;
    return {...currentDetail, shareholders: appended.length > 0 ? [...updated, ...appended] : updated};
}

/**
 * After Transfer / Allotment / CREATE on Tab N, refresh Tab N+1… drafts for display.
 * Does not copy pendingActions / draftRegistry (those stay on the origin YOA for save).
 * Pass `{omitZeroPerClient: true}` for MULTI Period Tabs (#20: default keeps all rows).
 */
export function buildIssuedCapitalForwardDisplayShareholders(
    priorShareholders: any[],
    opts?: {omitZeroPerClient?: boolean},
): any[] {
    const rows = Array.isArray(priorShareholders) ? priorShareholders : [];
    const source = opts?.omitZeroPerClient === true
        ? rows.filter(shouldRollIssuedCapitalShareholderForward)
        : rows;
    return source.map((p) => openIssuedCapitalCurrentFromPerClient(p));
}

/** Fingerprint of IC shareholders for prior→Current re-seed signatures. */
export function issuedCapitalShareholdersFingerprint(detail: any): string {
    const rows: any[] = Array.isArray(detail?.shareholders) ? detail.shareholders : [];
    return JSON.stringify(rows.map((r) => [
        r?.id ?? r?.shareholderId ?? null,
        shareholderNameKey(r),
        computeIssuedCapitalPerClientShares(r),
        computeIssuedCapitalPerClientPrice(r),
        num(r?.lastYearFinalSharesPrice),
        num(r?.currentShares),
        computeIssuedCapitalEffectiveCurrentSharesPrice(r),
    ]));
}

/**
 * Seed empty lastYearFinal from a scalar (FS last-year audited / prior-slice audited).
 * Applies to every empty row (typical single-line expense WP).
 */
export function seedLastYearFinalFromAmount(
    currentDetail: any,
    amount: number | null | undefined,
    opts: {rowsKey: string; onlyIfEmpty?: boolean},
): any | null {
    if (!currentDetail) return null;
    const ly = Number(amount);
    if (!Number.isFinite(ly) || ly === 0) return null;
    const rowsKey = opts.rowsKey;
    const onlyIfEmpty = opts.onlyIfEmpty !== false;
    const currRows: any[] = Array.isArray(currentDetail[rowsKey]) ? currentDetail[rowsKey] : [];
    if (currRows.length === 0) return null;

    let changed = false;
    const updated = currRows.map((row) => {
        if (onlyIfEmpty && !isEmptyLastYearFinal(row?.lastYearFinal)) return row;
        if (Number(row?.lastYearFinal) === ly) return row;
        changed = true;
        return {...row, lastYearFinal: ly};
    });
    if (!changed) return null;
    return {...currentDetail, [rowsKey]: updated};
}

function sumAdjAmountList(list: unknown): number {
    if (!Array.isArray(list) || list.length === 0) return 0;
    return list.reduce((sum: number, a: any) => sum + (Number(a?.adjustment) || 0), 0);
}

/**
 * MULTI WpPerCheck: Grand Total NBV c/f for one PPE Period Tab draft.
 * Includes display-mode nbvCf field adj for persisted rows (matches PropertySubTable totals).
 */
export function sumPpeDetailGrandNbvCf(
    detail: any,
    periodFrom?: string | null,
    periodTo?: string | null,
): number {
    if (!detail || typeof detail !== 'object') return 0;
    const rateObj = (detail.ppeRate && typeof detail.ppeRate === 'object') ? detail.ppeRate : {};
    let total = 0;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
        const rateField = PPE_RATE_BY_ROW_KEY[rowKey];
        const ratePct = Number(rateObj[rateField]) || 0;
        const rateFrac = ratePct > 1 ? ratePct / 100 : ratePct;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const {nbvCf} = computePpeRowClosingCf(row, rateFrac, periodFrom, periodTo);
            const includeAdj = row.id != null && Number(row.id) > 0;
            const nbvAdj = includeAdj ? sumAdjAmountList(row.adjustments?.nbvCfAdjs) : 0;
            total += nbvCf + nbvAdj;
        }
    }
    return total;
}

/**
 * MULTI WpPerCheck: Grand Total NBV b/f for one PPE Period Tab draft.
 * Aligns with PropertySubTable.computeRow nbvBf (+ cost/accDep field adj for persisted rows).
 */
export function sumPpeDetailGrandNbvBf(detail: any): number {
    if (!detail || typeof detail !== 'object') return 0;
    let total = 0;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = Array.isArray(detail[rowKey]) ? detail[rowKey] : [];
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const includeAdj = row.id != null && Number(row.id) > 0;
            const costAdj = includeAdj ? sumAdjAmountList(row.adjustments?.costAdjs) : 0;
            const accDepAdj = includeAdj ? sumAdjAmountList(row.adjustments?.accDepAdjs) : 0;
            const costBf = num(row.costBf) + costAdj;
            const accDepBf = num(row.accDepBf) + accDepAdj;
            // Legacy MULTI: costBf cell held prior NBV — NBV b/f is that value.
            const isPriorNbvCost = !!(row.ppeCostBfIsPriorNbv && !row.ppeCostBfIsCostBf);
            total += isPriorNbvCost ? costBf : costBf - accDepBf;
        }
    }
    return total;
}

/** Resolve intangibleRows / ITL section arrays from an IA or ITL working-paper detail. */
function extractIntangibleRowsFromDetail(detail: any): any[] {
    if (!detail || typeof detail !== 'object') return [];
    const hasItlSections = ITL_ROW_ARRAY_KEYS.some((k) => Array.isArray(detail[k]));
    if (hasItlSections) {
        return ITL_ROW_ARRAY_KEYS.flatMap((k) => (Array.isArray(detail[k]) ? detail[k] : []));
    }
    if (Array.isArray(detail.intangibleRows)) return detail.intangibleRows;
    if (Array.isArray(detail.intangibleRow)) return detail.intangibleRow;
    return [];
}

/**
 * MULTI WpPerCheck: Grand Total NBV c/f for one IA / ITL Period Tab draft.
 * Aligns with computeIntangibleDerived (+ display-mode nbvCfAdj for persisted rows).
 */
export function sumIaDetailGrandNbvCf(detail: any): number {
    const rows = extractIntangibleRowsFromDetail(detail);
    let total = 0;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const {nbvCf} = computeIaClosingCf(row);
        const includeAdj = row.id != null && Number(row.id) > 0;
        const nbvAdj = includeAdj ? sumAdjAmountList(row.adjustments?.nbvCfAdjs) : 0;
        total += nbvCf + nbvAdj;
    }
    return total;
}

/**
 * MULTI WpPerCheck: Grand Total NBV b/f for one IA / ITL Period Tab draft.
 * Aligns with computeIntangibleDerived.nbvBf (costBf − accAmorBf).
 */
export function sumIaDetailGrandNbvBf(detail: any): number {
    const rows = extractIntangibleRowsFromDetail(detail);
    let total = 0;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        total += num(row.costBf) - num(row.accAmorBf);
    }
    return total;
}

/** Shift YOA `YYYY/YY` back one assessment year (engagement lastYoa from first-tab YOA). */
export function priorYearOfAssessment(yoa: string | null | undefined): string | null {
    const raw = String(yoa || '').trim();
    const m = /^(\d{4})\s*\/\s*(\d{2})$/.exec(raw);
    if (!m) return null;
    const left = Number(m[1]) - 1;
    const right = (Number(m[2]) - 1 + 100) % 100;
    return `${left}/${String(right).padStart(2, '0')}`;
}

/**
 * MULTI WpPerCheck: c/f ← last Tab Grand Total NBV c/f;
 * b/f ← first Tab Grand Total NBV b/f when sumDetailNbvBf/activeNbvBf provided,
 * otherwise legacy first-tab NBV c/f (call-site opt-in; SINGLE callers never hit this).
 * c/f YOA label ← last period (stable);
 * b/f YOA label ← engagement lastYoa (first-tab fallback or prior of first-period YOA) — never tab-shifted.
 * Per BS stays annual parent FS (WpPerCheck conventional lookup) — not period-tab Audited.
 */
export function resolveMultiWpPerCheckNbvEndpoints(opts: {
    periods: Array<{
        key?: string;
        yearOfAssessment?: string | null;
        periodFrom?: string | null;
        periodTo?: string | null;
    }>;
    activeSliceKey?: string | null;
    activeNbvCf: number;
    /** When set with sumDetailNbvBf: first-tab NBV b/f for lastYearValue. */
    activeNbvBf?: number;
    getDetailForPeriod: (period: {
        key?: string;
        yearOfAssessment?: string | null;
        periodFrom?: string | null;
        periodTo?: string | null;
    }) => any;
    sumDetailNbvCf: (
        detail: any,
        period: {periodFrom?: string | null; periodTo?: string | null},
    ) => number;
    /** When set with activeNbvBf: first-tab NBV b/f for lastYearValue. */
    sumDetailNbvBf?: (
        detail: any,
        period: {periodFrom?: string | null; periodTo?: string | null},
    ) => number;
    fallbackYoa?: string | null;
    /** Parent WP meta.lastYoa (annual lastYear) — preferred for b/f column title when set. */
    fallbackLastYoa?: string | null;
}): {
    currentValue: number;
    lastYearValue: number;
    yoaLabel: string | null;
    lastYoaLabel: string | null;
} | null {
    const periods = Array.isArray(opts.periods) ? opts.periods.filter((p) => !!p?.key) : [];
    if (periods.length < 2) return null;
    const first = periods[0];
    const last = periods[periods.length - 1];
    const activeKey = String(opts.activeSliceKey || '').trim();
    const firstKey = String(first.key || '').trim();
    const lastKey = String(last.key || '').trim();
    const useFirstTabNbvBf = opts.sumDetailNbvBf != null && opts.activeNbvBf != null;

    const nbvCfFor = (period: typeof first, isActive: boolean): number => {
        if (isActive) return Number(opts.activeNbvCf) || 0;
        return opts.sumDetailNbvCf(opts.getDetailForPeriod(period), {
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
        });
    };
    const nbvBfFor = (period: typeof first, isActive: boolean): number => {
        if (isActive) return Number(opts.activeNbvBf) || 0;
        return opts.sumDetailNbvBf!(opts.getDetailForPeriod(period), {
            periodFrom: period.periodFrom,
            periodTo: period.periodTo,
        });
    };

    const onFirstTab = !!activeKey && activeKey === firstKey;
    const fallbackLastYoa = opts.fallbackLastYoa ? String(opts.fallbackLastYoa).trim() : '';
    // First Tab meta.lastYoa / Tax annualLastYoa is engagement last year; other Tabs' lastYoa is prior-slice (tab-shifted) — ignore.
    const derivedLastYoa = priorYearOfAssessment(first.yearOfAssessment);
    const lastYoaLabel = (onFirstTab && fallbackLastYoa)
        || derivedLastYoa
        || (onFirstTab ? fallbackLastYoa : '')
        || first.yearOfAssessment
        || null;

    return {
        currentValue: nbvCfFor(last, !!activeKey && activeKey === lastKey),
        lastYearValue: useFirstTabNbvBf
            ? nbvBfFor(first, !!activeKey && activeKey === firstKey)
            : nbvCfFor(first, !!activeKey && activeKey === firstKey),
        yoaLabel: last.yearOfAssessment || opts.fallbackYoa || null,
        lastYoaLabel,
    };
}

// ---------------------------------------------------------------------------
// Rental Expenses / Income — MULTI forward roll (lease from–to overlap gate)
// ---------------------------------------------------------------------------

export type RentalPeriodSlice = PpePeriodSlice;

function unwrapRentalDetailBag(raw: any): any | null {
    if (!raw || typeof raw !== 'object') return null;
    if (Array.isArray(raw.details) && raw.details[0] && typeof raw.details[0] === 'object' && !Array.isArray(raw.details[0])) {
        return {...raw.details[0]};
    }
    if (raw.details && typeof raw.details === 'object' && !Array.isArray(raw.details)) {
        return {...raw.details};
    }
    return raw;
}

function rentalRowsFromDetail(detail: any): any[] {
    const bag = unwrapRentalDetailBag(detail) ?? detail;
    return Array.isArray(bag?.rows) ? bag.rows : [];
}

/** True when row is a MULTI rolled display of an earlier-Tab lease origin. */
export function isRentalRolledRow(row: any): boolean {
    // Only the explicit client flag — do not treat stray rentalOriginKey as rolled (SINGLE-safe).
    return row?.rentalIsRolled === true;
}

/**
 * Whether lease identity (edit/delete) is locked on the active Period Tab.
 * - Total / seamless: explicit {@code rentalIsRolled}
 * - PL Multiple (single WP tab): lease began before this period and overlaps a prior period
 *   → only the origin year Tab may delete/edit identity (same UX as Total).
 * - First period tab / true SINGLE: never locked by period continuity.
 */
export function shouldLockRentalIdentityOnPeriod(
    row: any,
    periods: Array<{key?: string; periodFrom?: string; periodTo?: string} | null> | null | undefined,
    activeSliceKey: string | null | undefined,
): boolean {
    if (isRentalRolledRow(row)) return true;
    if (!Array.isArray(periods) || periods.length < 2) return false;
    const sliceKey = String(activeSliceKey || '').trim();
    if (!sliceKey) return false;
    const idx = periods.findIndex((p) => p?.key && String(p.key) === sliceKey);
    if (idx <= 0) return false;

    const active = periods[idx];
    const pf = parseYMD(active?.periodFrom);
    const bounds = hasActiveRenewal(row)
        ? getMergedLeaseDateBounds(row, {applyCancellation: false})
        : null;
    const from = parseYMD(bounds?.fromDate || row?.fromDate);
    if (!pf || !from) return false;
    // Created / starting in this period → origin Tab for this lease
    if (from.getTime() >= pf.getTime()) return false;

    for (let i = 0; i < idx; i++) {
        if (rentalLeaseOverlapsPeriod(row, periods[i]?.periodFrom, periods[i]?.periodTo)) {
            return true;
        }
    }
    return false;
}

function isRentalOriginRow(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (isRentalRolledRow(row)) return false;
    const bounds = hasActiveRenewal(row) ? getMergedLeaseDateBounds(row, {applyCancellation: false}) : null;
    const from = parseYMD(bounds?.fromDate || row.fromDate);
    const to = parseYMD(bounds?.toDate || row.toDate);
    return !!(from && to);
}

/** Lease from–to (incl. renewal merge) overlaps the Period Tab window. */
export function rentalLeaseOverlapsPeriod(
    row: any,
    periodFrom?: string | null,
    periodTo?: string | null,
): boolean {
    if (!row) return false;
    const bounds = hasActiveRenewal(row) ? getMergedLeaseDateBounds(row, {applyCancellation: false}) : null;
    const rowFrom = parseYMD(bounds?.fromDate || row.fromDate);
    const rowTo = parseYMD(bounds?.toDate || row.toDate);
    const from = parseYMD(periodFrom);
    const to = parseYMD(periodTo);
    if (!rowFrom || !rowTo || !from || !to) return false;
    return overlapMonthsInclusive(rowFrom, rowTo, from, to) > 0;
}

function rentalOriginKey(sliceKey: string, row: any, index: number): string {
    if (row?.rentalOriginKey) return String(row.rentalOriginKey);
    if (row?.id != null && Number(row.id) !== 0) return `${sliceKey}#id:${row.id}`;
    const addr = String(row?.address || '').trim().toLowerCase();
    return `${sliceKey}#i:${index}|${row?.fromDate || ''}|${row?.toDate || ''}|${addr}`;
}

/** Public helper: stable origin identity for MULTI forward-roll / delete sync. */
export function buildRentalOriginKey(sliceKey: string, row: any, index: number): string {
    return rentalOriginKey(sliceKey, row, index);
}

function isBlankRentalRow(row: any): boolean {
    if (!row || typeof row !== 'object') return true;
    const hasSavedId = row.id != null && Number(row.id) > 0;
    if (hasSavedId) return false;
    if (isRentalRolledRow(row)) return false;
    const hasAddress = String(row.address || '').trim() !== '';
    const hasLandlord = String(row.landlord || '').trim() !== '';
    const hasTenant = String(row.tenant || '').trim() !== '';
    const hasFrom = String(row.fromDate || '').trim() !== '';
    const hasTo = String(row.toDate || '').trim() !== '';
    const hasRent = String(row.monthlyRent || '').trim() !== '';
    const hasAgreement = String(row.agreementDate || '').trim() !== '';
    return !hasAddress && !hasLandlord && !hasTenant && !hasFrom && !hasTo && !hasRent && !hasAgreement;
}

function isBlankRentalRows(rows: any[] | null | undefined): boolean {
    if (!Array.isArray(rows) || rows.length === 0) return true;
    return rows.every(isBlankRentalRow);
}

function cloneRenewalSegment(renewal: any, preserve?: {id?: number; clientKey?: string} | null): any | null {
    if (!renewal || typeof renewal !== 'object') return null;
    if (!hasActiveRenewal({renewal})) return null;
    return {
        fromDate: String(renewal.fromDate || ''),
        toDate: String(renewal.toDate || ''),
        monthlyRent: String(renewal.monthlyRent || ''),
        // Do not carry second-segment DB id from origin Tab unless this Tab already saved one
        id: preserve?.id,
        clientKey: preserve?.clientKey
            || renewal.clientKey
            || `rk-ren-${Math.random().toString(36).slice(2, 10)}`,
    };
}

/** Copy lease identity/detail onto a later Tab; clear period-local drawer / adj / DB id. */
function rollRentalRowFromOrigin(prior: any, originKey: string, code: string): any {
    return {
        clientKey: `rk-roll-${Math.random().toString(36).slice(2, 10)}`,
        code: String(prior?.code || code || ''),
        address: String(prior?.address || ''),
        agreementDate: String(prior?.agreementDate || ''),
        landlord: String(prior?.landlord || ''),
        tenant: String(prior?.tenant || ''),
        leaseAgreementLink: prior?.leaseAgreementLink ?? null,
        pendingDeleteLeaseAgreementLink: null,
        tenantPersonId: prior?.tenantPersonId ?? null,
        tenantPersonIdNo: prior?.tenantPersonIdNo ?? null,
        rentalPeriod: String(prior?.rentalPeriod || ''),
        fromDate: String(prior?.fromDate || ''),
        toDate: String(prior?.toDate || ''),
        monthlyRent: String(prior?.monthlyRent || ''),
        rentFreePeriods: normalizeRentFreePeriods(prior?.rentFreePeriods),
        cancellationDate: String(prior?.cancellationDate || ''),
        rentalDepositChange: prior?.rentalDepositChange ?? null,
        renewal: cloneRenewalSegment(prior?.renewal),
        otherInfo: {},
        headerId: '',
        adjustments: null,
        rentalIsRolled: true,
        rentalOriginKey: originKey,
    };
}

type RentalOriginRoll = {key: string; rolled: any};

function collectRentalOriginRolls(
    periods: RentalPeriodSlice[],
    activeIndex: number,
    getDetail: (draftKey: string) => any,
    apiCode: string,
    activePeriod: {periodFrom?: string; periodTo?: string},
): RentalOriginRoll[] {
    const out: RentalOriginRoll[] = [];
    for (let i = 0; i < activeIndex; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        const priorRows = rentalRowsFromDetail(detail);
        if (priorRows.length === 0) continue;
        priorRows.forEach((prior: any, index: number) => {
            if (!isRentalOriginRow(prior)) return;
            if (!rentalLeaseOverlapsPeriod(prior, activePeriod.periodFrom, activePeriod.periodTo)) return;
            // When next-tab All Rental Details Total would be 0, skip auto-generate
            const nextPeriodTotal = computeRentalRowAllDetailsTotal(
                prior,
                activePeriod.periodFrom,
                activePeriod.periodTo,
            );
            if (Math.abs(Number(nextPeriodTotal) || 0) < 0.005) return;
            const key = rentalOriginKey(slice.key, prior, index);
            out.push({key, rolled: rollRentalRowFromOrigin(prior, key, apiCode)});
        });
    }
    return out;
}

function findRentalOriginRollForRow(row: any, originRolls: RentalOriginRoll[]): RentalOriginRoll | null {
    if (!originRolls.length) return null;
    if (row?.rentalOriginKey) {
        const byKey = originRolls.find((o) => o.key === row.rentalOriginKey);
        if (byKey) return byKey;
    }
    const addr = String(row?.address || '').trim().toLowerCase();
    const from = String(row?.fromDate || '').trim();
    const to = String(row?.toDate || '').trim();
    if (addr || from || to) {
        const byLease = originRolls.find((o) => {
            const sameAddr = String(o.rolled?.address || '').trim().toLowerCase() === addr;
            const sameFrom = String(o.rolled?.fromDate || '').trim() === from;
            const sameTo = String(o.rolled?.toDate || '').trim() === to;
            if (addr && from && to) return sameAddr && sameFrom && sameTo;
            if (from && to) return sameFrom && sameTo && (!addr || sameAddr);
            return false;
        });
        if (byLease) return byLease;
    }
    return null;
}

function applyRolledRentalDetailFields(row: any, match: RentalOriginRoll): any {
    const rolled = match.rolled;
    return {
        ...row,
        address: rolled.address,
        agreementDate: rolled.agreementDate,
        landlord: rolled.landlord,
        tenant: rolled.tenant,
        leaseAgreementLink: rolled.leaseAgreementLink,
        tenantPersonId: rolled.tenantPersonId,
        tenantPersonIdNo: rolled.tenantPersonIdNo,
        rentalPeriod: rolled.rentalPeriod,
        fromDate: rolled.fromDate,
        toDate: rolled.toDate,
        monthlyRent: rolled.monthlyRent,
        rentFreePeriods: normalizeRentFreePeriods(rolled.rentFreePeriods),
        cancellationDate: rolled.cancellationDate,
        rentalDepositChange: rolled.rentalDepositChange,
        renewal: cloneRenewalSegment(rolled.renewal, row?.renewal
            ? {id: row.renewal.id, clientKey: row.renewal.clientKey}
            : null),
        rentalIsRolled: true,
        rentalOriginKey: match.key,
        // Keep local clientKey / id / otherInfo / adjustments (period-local)
    };
}

function renewalIdentityPayload(renewal: any): unknown {
    if (!renewal || !hasActiveRenewal({renewal})) return null;
    return {
        fromDate: String(renewal.fromDate || ''),
        toDate: String(renewal.toDate || ''),
        monthlyRent: String(renewal.monthlyRent || ''),
    };
}

function rentalDetailIdentityEqual(a: any, b: any): boolean {
    return String(a?.address || '') === String(b?.address || '')
        && String(a?.agreementDate || '') === String(b?.agreementDate || '')
        && String(a?.landlord || '') === String(b?.landlord || '')
        && String(a?.tenant || '') === String(b?.tenant || '')
        && String(a?.fromDate || '') === String(b?.fromDate || '')
        && String(a?.toDate || '') === String(b?.toDate || '')
        && String(a?.monthlyRent || '') === String(b?.monthlyRent || '')
        && String(a?.cancellationDate || '') === String(b?.cancellationDate || '')
        && String(a?.rentalPeriod || '') === String(b?.rentalPeriod || '')
        && JSON.stringify(normalizeRentFreePeriods(a?.rentFreePeriods))
            === JSON.stringify(normalizeRentFreePeriods(b?.rentFreePeriods))
        && JSON.stringify(renewalIdentityPayload(a?.renewal))
            === JSON.stringify(renewalIdentityPayload(b?.renewal))
        && a?.rentalOriginKey === b?.rentalOriginKey
        && !!a?.rentalIsRolled === !!b?.rentalIsRolled;
}

/** Fingerprint of lease origins on all Tabs before active — drives re-roll when any prior changes. */
export function rentalAllPriorOriginsFingerprint(
    periods: RentalPeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): string {
    if (!Array.isArray(periods) || !activeSliceKey || !apiCode) return '';
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex <= 0) return '';
    const active = periods[activeIndex];
    const payload: unknown[] = [];
    for (let i = 0; i < activeIndex; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const detail = getDetail(`${apiCode}__${slice.key}`);
        const rows = rentalRowsFromDetail(detail);
        rows.forEach((row: any, index: number) => {
            if (!isRentalOriginRow(row)) return;
            if (!rentalLeaseOverlapsPeriod(row, active?.periodFrom, active?.periodTo)) return;
            const periodTotal = computeRentalRowAllDetailsTotal(row, active?.periodFrom, active?.periodTo);
            if (Math.abs(Number(periodTotal) || 0) < 0.005) return;
            payload.push({
                k: rentalOriginKey(slice.key, row, index),
                address: row.address,
                fromDate: row.fromDate,
                toDate: row.toDate,
                monthlyRent: row.monthlyRent,
                landlord: row.landlord,
                tenant: row.tenant,
                agreementDate: row.agreementDate,
                cancellationDate: row.cancellationDate,
                rentFreePeriods: row.rentFreePeriods,
                renewal: row.renewal,
                rentalDepositChange: row.rentalDepositChange,
                periodTotal,
            });
        });
    }
    return JSON.stringify(payload);
}

/** Fingerprint of rental rows — include in roll-forward signature to avoid stale-skip races. */
export function rentalDetailRowsFingerprint(detail: any): string {
    const rows = rentalRowsFromDetail(detail);
    return JSON.stringify(rows.map((r: any) => ({
        id: r?.id,
        clientKey: r?.clientKey,
        rentalOriginKey: r?.rentalOriginKey,
        rentalIsRolled: r?.rentalIsRolled,
        address: r?.address,
        fromDate: r?.fromDate,
        toDate: r?.toDate,
        monthlyRent: r?.monthlyRent,
        landlord: r?.landlord,
        tenant: r?.tenant,
        renewal: r?.renewal,
    })));
}

/**
 * MULTI forward roll onto the active Tab from earlier Tabs' lease origins
 * whose from–to overlaps this Tab's period. Forward-only.
 *
 * - Tab N origin (from+to set) overlapping Tab M → Tab M gets a rolled detail row
 * - Local origins that do not match any prior stay editable on this Tab
 */
export function applyRentalForwardRollFromPriors(
    currentDetail: any,
    periods: RentalPeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    getDetail: (draftKey: string) => any,
    apiCode: string,
): any | null {
    if (!currentDetail || !Array.isArray(periods) || !activeSliceKey || !apiCode) return null;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex <= 0) return null;
    const active = periods[activeIndex];
    if (!active) return null;

    const bag = unwrapRentalDetailBag(currentDetail) ?? currentDetail;
    const currRows = Array.isArray(bag?.rows) ? bag.rows : (Array.isArray(currentDetail.rows) ? currentDetail.rows : []);
    const originRolls = collectRentalOriginRolls(
        periods,
        activeIndex,
        getDetail,
        apiCode,
        {periodFrom: active.periodFrom, periodTo: active.periodTo},
    );

    if (originRolls.length === 0) {
        // Drop orphaned rolled rows when no overlapping prior origins remain
        if (!Array.isArray(currRows) || currRows.length === 0) return null;
        const kept = currRows.filter((row: any) => !isRentalRolledRow(row));
        if (kept.length === currRows.length) return null;
        return {
            ...currentDetail,
            rows: kept.length > 0 ? kept : [{
                clientKey: `rk-${Math.random().toString(36).slice(2, 10)}`,
                address: '',
                agreementDate: '',
                landlord: '',
                tenant: '',
                rentalPeriod: '',
                fromDate: '',
                toDate: '',
                monthlyRent: '',
                rentFreePeriods: [{from: '', to: ''}],
                code: apiCode,
                otherInfo: {},
                headerId: '',
            }],
        };
    }

    if (isBlankRentalRows(currRows)) {
        return {...currentDetail, rows: originRolls.map((o) => o.rolled)};
    }

    const usedKeys = new Set<string>();
    let changed = false;
    const updated: any[] = [];
    const activeKeyPrefix = `${activeSliceKey}#`;

    for (const row of currRows) {
        const match = findRentalOriginRollForRow(row, originRolls);

        if (!isRentalRolledRow(row)) {
            if (match && !match.key.startsWith(activeKeyPrefix)) {
                if (usedKeys.has(match.key)) {
                    changed = true;
                    continue;
                }
                usedKeys.add(match.key);
                changed = true;
                updated.push(applyRolledRentalDetailFields(row, match));
                continue;
            }
            if (match) usedKeys.add(match.key);
            updated.push(row);
            continue;
        }

        if (match) {
            if (usedKeys.has(match.key)) {
                changed = true;
                continue;
            }
            usedKeys.add(match.key);
            const nextRow = applyRolledRentalDetailFields(row, match);
            if (rentalDetailIdentityEqual(row, nextRow)) {
                updated.push(row);
            } else {
                changed = true;
                updated.push(nextRow);
            }
            continue;
        }

        // Orphaned rolled row — origin deleted or no longer overlaps this Tab
        if (row?.rentalOriginKey || row?.rentalIsRolled) {
            changed = true;
            continue;
        }
        updated.push(row);
    }

    const appended = originRolls
        .filter((o) => !usedKeys.has(o.key))
        .map((o) => o.rolled);
    if (appended.length > 0 || changed) {
        return {
            ...currentDetail,
            rows: appended.length > 0 ? [...updated, ...appended] : updated,
        };
    }
    return null;
}

/**
 * MULTI: after deleting an origin row on Tab N, remove matching rolled display rows
 * from all later Period Tab drafts (N+1…).
 */
export function removeRentalOriginFromLaterTabs(
    periods: RentalPeriodSlice[] | null | undefined,
    activeSliceKey: string | null | undefined,
    apiCode: string,
    originKey: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
    deletedRow?: {address?: string | null; fromDate?: string | null; toDate?: string | null} | null,
): void {
    if (!originKey || !apiCode || !Array.isArray(periods) || !activeSliceKey) return;
    const activeIndex = periods.findIndex((p) => p.key === activeSliceKey);
    if (activeIndex < 0 || activeIndex >= periods.length - 1) return;

    const delAddr = String(deletedRow?.address || '').trim().toLowerCase();
    const delFrom = String(deletedRow?.fromDate || '').trim();
    const delTo = String(deletedRow?.toDate || '').trim();

    for (let i = activeIndex + 1; i < periods.length; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const draftKey = `${apiCode}__${slice.key}`;
        const detail = getDetail(draftKey);
        if (!detail || typeof detail !== 'object') continue;
        const rows = rentalRowsFromDetail(detail);
        if (!rows.length) continue;

        const filtered = rows.filter((row: any) => {
            if (row?.rentalOriginKey && String(row.rentalOriginKey) === originKey) return false;
            // Persisted rolled rows lose rentalIsRolled/rentalOriginKey on save — also match
            // by lease identity (address + from–to) so backend-loaded later-year rows delete too.
            if (
                delFrom
                && delTo
                && String(row.fromDate || '').trim() === delFrom
                && String(row.toDate || '').trim() === delTo
                && (!delAddr || String(row.address || '').trim().toLowerCase() === delAddr)
            ) {
                return false;
            }
            return true;
        });
        if (filtered.length === rows.length) continue;

        const nextRows = filtered.length > 0
            ? filtered
            : [{
                clientKey: `rk-${Math.random().toString(36).slice(2, 10)}`,
                address: '',
                agreementDate: '',
                landlord: '',
                tenant: '',
                rentalPeriod: '',
                fromDate: '',
                toDate: '',
                monthlyRent: '',
                rentFreePeriods: [{from: '', to: ''}],
                code: apiCode,
                otherInfo: {},
                headerId: '',
            }];
        const next = {...detail, rows: nextRows};
        try {
            setDetail(draftKey, typeof structuredClone === 'function'
                ? structuredClone(next)
                : JSON.parse(JSON.stringify(next)));
        } catch {
            setDetail(draftKey, JSON.parse(JSON.stringify(next)));
        }
    }
}

/**
 * MULTI save: re-apply forward roll on every non-first Period Tab so later YOA
 * buckets (rolled lease rows seeded from prior origins) are present even when
 * the user never opened that Tab this session. Mirrors syncPpeForwardRollAcrossPeriods.
 */
export function syncRentalForwardRollAcrossPeriods(
    periods: RentalPeriodSlice[] | null | undefined,
    apiCode: string,
    getDetail: (draftKey: string) => any,
    setDetail: (draftKey: string, detail: any) => void,
): void {
    if (!apiCode || !Array.isArray(periods) || periods.length < 2) return;
    for (let i = 1; i < periods.length; i++) {
        const slice = periods[i];
        if (!slice?.key) continue;
        const draftKey = `${apiCode}__${slice.key}`;
        const current = getDetail(draftKey);
        if (!current || typeof current !== 'object') continue;
        const rolled = applyRentalForwardRollFromPriors(
            current,
            periods,
            slice.key,
            getDetail,
            apiCode,
        );
        if (!rolled) continue;
        try {
            setDetail(
                draftKey,
                typeof structuredClone === 'function'
                    ? structuredClone(rolled)
                    : JSON.parse(JSON.stringify(rolled)),
            );
        } catch {
            setDetail(draftKey, JSON.parse(JSON.stringify(rolled)));
        }
    }
}
